import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Menu, X, Globe } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#ella", label: "Ellas Geschichte" },
    { href: "#rett-info", label: "Rett-Syndrom" },
    { href: "#event", label: "RunforRett" },
    { href: "/spendenlauf", label: "Spendenlauf-Infos", isPage: true },
  ];

  const scrollTo = (href) => {
    setMobileOpen(false);
    if (href.startsWith("/")) {
      navigate(href);
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass-nav shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <button
            data-testid="nav-logo"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="flex items-center gap-2 shrink-0"
          >
            <img
              src="/logo-runforrett.svg"
              alt="Run for Rett Logo"
              className="h-12 md:h-14 w-auto"
            />
          </button>

          {/* Desktop Links */}
          <div className="hidden md:flex items-center gap-6 lg:gap-8">
            {links.map((link) => (
              <button
                key={link.href}
                data-testid={`nav-link-${link.href.slice(1)}`}
                onClick={() => scrollTo(link.href)}
                className="text-sm font-medium text-gray-600 hover:text-[#b46eb6] transition-colors"
              >
                {link.label}
              </button>
            ))}
            <Button
              data-testid="nav-donate-btn"
              onClick={() => scrollTo("#spenden")}
              className="rounded-full px-6 font-semibold bg-[#b46eb6] hover:bg-[#8f5691] text-white"
            >
              Spenden
            </Button>
            <Button
              data-testid="nav-lang-btn"
              variant="ghost"
              onClick={() => navigate("/en")}
              className="rounded-full px-4 text-gray-500 hover:text-[#b46eb6] hover:bg-[#b46eb6]/5 text-sm font-medium"
              title="English"
            >
              <Globe className="w-4 h-4 mr-1.5" /> English
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="md:hidden glass-nav border-t border-gray-100"
        >
          <div className="px-4 py-4 space-y-3">
            {links.map((link) => (
              <button
                key={link.href}
                onClick={() => scrollTo(link.href)}
                className="block w-full text-left px-3 py-2 text-sm font-medium text-gray-700 hover:text-[#b46eb6] hover:bg-purple-50 rounded-lg transition-colors"
              >
                {link.label}
              </button>
            ))}
            <Button
              onClick={() => scrollTo("#spenden")}
              className="w-full rounded-full font-semibold bg-[#b46eb6] hover:bg-[#8f5691] text-white"
            >
              Spenden
            </Button>
            <button
              onClick={() => { setMobileOpen(false); navigate("/en"); }}
              className="flex items-center gap-2 w-full px-3 py-2 text-sm font-medium text-gray-700 hover:text-[#b46eb6] hover:bg-purple-50 rounded-lg transition-colors"
            >
              <Globe className="w-4 h-4" /> English
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}
